import React, { memo, useState } from 'react';
import { Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import type { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { UploadApi } from '../../apis';
import { useSelector } from 'react-redux';
import { AuthState } from '../../store/reducer/auth';

type UploadResponse = {
  url?: string; // сервер буцаах URL (жишээ)
  path?: string; // эсвэл path
  message?: string; // нэмэлт мессеж
  [key: string]: any;
  error?: any;
};

const getBase64 = (file: RcFile) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
  });

const UploadImage = memo(({ callBack, error }: { callBack: (url: string) => void; error: any }) => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const { accessToken } = useSelector((state: { auth: AuthState }) => state?.auth);

  // 1) Файл төрөл/хэмжээ шалгах
  const beforeUpload = (file: RcFile) => {
    const isImage =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/webp' ||
      file.type === 'image/jpg';

    if (!isImage) {
      message.error('Зөвшөөрөгдсөн зураг: JPG / JPEG / PNG / WEBP');
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Зургийн хэмжээ 2MB-аас бага байх ёстой');
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // 2) Жинхэнэ upload-ыг өөрөө хийх (FormData: file: file)
  const customRequest = async (options: RcCustomRequestOptions) => {
    const { file, onError, onSuccess } = options;

    try {
      setLoading(true);

      // Local preview
      const preview = await getBase64(file as RcFile);
      setImageUrl(preview);

      const formData = new FormData();
      formData.append('file', file as Blob); // <-- file: file

      //   const res = await UploadApi.upload(formData);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/media/upload`, // таны upload API
        {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!res.ok) {
        throw new Error(`Upload failed with status ${res.status}`);
      }

      const data: UploadResponse = await res.json();

      callBack && callBack(data?.url!);

      // Серверээс ирэх талбарын нэрийг энд тааруул
      const remoteUrl = data.url || data.path;
      if (remoteUrl) {
        setImageUrl(remoteUrl); // Серверийн жинхэнэ URL-ыг харуулах
      }

      onSuccess?.(data, new XMLHttpRequest());
      message.success('Амжилттай илгээлээ');
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || 'Илгээх үед алдаа гарлаа');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const uploadButton = (
    <button type="button" style={{ border: 0, background: 'none' }}>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8, fontSize: 9 }}>{loading ? 'Илгээж байна.' : 'Зураг оруулах'}</div>
    </button>
  );

  return (
    <div className="flex flex-col w-[80px] h-[80px]">
      <div
        className={
          error
            ? 'border-red-500 border-[1.5px] overflow-hidden h-[80px] w-80px'
            : 'overflow-hidden h-[80px] w-80px'
        }
      >
        <Upload
          accept=".png, .jpg, .jpeg, .webp"
          listType="picture-card"
          className="upload-image"
          showUploadList={false}
          name="file"
          beforeUpload={beforeUpload}
          customRequest={customRequest}
          maxCount={1}
          style={{ width: '80px', height: '80px' }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="avatar" style={{ width: '100%', objectFit: 'cover' }} />
          ) : (
            uploadButton
          )}
        </Upload>
      </div>
    </div>
  );
});

export { UploadImage };
